import { normalizeCountryName } from '@/utils/normalizeCountryName';

export { normalizeCountryName } from '@/utils/normalizeCountryName';

type SubdivisionNameMap = Record<string, string>;

interface CountryJurisdictionConfig {
  /**
   * Optional display order for subdivisions. Codes are sorted by their position
   * here before falling back to localized/canonical name ordering.
   */
  subdivisionOrder?: string[];
  /**
   * Optional list of canonical codes to expose for this country.
   * If omitted, we use all codes from iso-3166-2.
   */
  allowedCodes?: string[];
  /**
   * Optional canonical English names keyed by canonical code.
   */
  canonicalNames?: SubdivisionNameMap;
  /**
   * Optional mapping from legacy/alternate code -> canonical code.
   */
  codeAliases?: Record<string, string>;
}

const JAPAN_PREFECTURE_ORDER = [
  'JP-01',
  'JP-02',
  'JP-03',
  'JP-04',
  'JP-05',
  'JP-06',
  'JP-07',
  'JP-08',
  'JP-09',
  'JP-10',
  'JP-11',
  'JP-12',
  'JP-13',
  'JP-14',
  'JP-15',
  'JP-16',
  'JP-17',
  'JP-18',
  'JP-19',
  'JP-20',
  'JP-21',
  'JP-22',
  'JP-23',
  'JP-24',
  'JP-25',
  'JP-26',
  'JP-27',
  'JP-28',
  'JP-29',
  'JP-30',
  'JP-31',
  'JP-32',
  'JP-33',
  'JP-34',
  'JP-35',
  'JP-36',
  'JP-37',
  'JP-38',
  'JP-39',
  'JP-40',
  'JP-41',
  'JP-42',
  'JP-43',
  'JP-44',
  'JP-45',
  'JP-46',
  'JP-47',
];

const COUNTRY_JURISDICTION_CONFIG_BY_COUNTRY: Record<string, CountryJurisdictionConfig> =
  {
    Japan: {
      subdivisionOrder: JAPAN_PREFECTURE_ORDER,
    },
  };

const getCountryJurisdictionConfig = (
  countryName: string | null | undefined
): CountryJurisdictionConfig | null => {
  const normalizedCountryName = normalizeCountryName(countryName);
  if (!normalizedCountryName) return null;

  return COUNTRY_JURISDICTION_CONFIG_BY_COUNTRY[normalizedCountryName] ?? null;
};

export const getCanonicalSubdivisionName = ({
  countryName,
  code,
  fallbackName,
}: {
  countryName: string | null | undefined;
  code: string;
  fallbackName: string;
}): string => {
  const config = getCountryJurisdictionConfig(countryName);
  return config?.canonicalNames?.[code] ?? fallbackName;
};

export const normalizeSubdivisionCode = ({
  countryName,
  code,
}: {
  countryName: string | null | undefined;
  code: string;
}): string => {
  const config = getCountryJurisdictionConfig(countryName);
  if (!config?.codeAliases) return code;

  return config.codeAliases[code] ?? code;
};

export const getLegacySubdivisionLookupCode = ({
  countryName,
  code,
}: {
  countryName: string | null | undefined;
  code: string;
}): string | null => {
  const config = getCountryJurisdictionConfig(countryName);
  if (!config?.codeAliases) return null;

  const reverseAliasEntry = Object.entries(config.codeAliases).find(
    ([, canonicalCode]) => canonicalCode === code
  );
  return reverseAliasEntry?.[0] ?? null;
};

export const getAllowedSubdivisionCodesForCountry = (
  countryName: string | null | undefined
): string[] | null => {
  const config = getCountryJurisdictionConfig(countryName);
  if (!config?.allowedCodes) return null;

  return Array.from(
    new Set(
      config.allowedCodes.map((code) => normalizeSubdivisionCode({ countryName, code }))
    )
  );
};

export const getExpectedSubdivisionCodesForCountry = (
  countryName: string | null | undefined
): string[] => {
  return getAllowedSubdivisionCodesForCountry(countryName) ?? [];
};

export const getSubdivisionTranslationKey = (code: string) =>
  `jurisdiction.${code.replace(/-/g, '_')}`;

export const getSubdivisionSortIndex = ({
  countryName,
  code,
}: {
  countryName: string | null | undefined;
  code: string;
}): number | null => {
  const config = getCountryJurisdictionConfig(countryName);
  if (!config?.subdivisionOrder) return null;

  const normalizedCode = normalizeSubdivisionCode({ countryName, code });
  const index = config.subdivisionOrder.indexOf(normalizedCode);

  return index === -1 ? null : index;
};

interface SortableSubdivision {
  code: string;
  name: string;
}

export const compareSubdivisionsForCountry = (
  countryName: string | null | undefined,
  a: SortableSubdivision,
  b: SortableSubdivision
): number => {
  const aSortIndex = getSubdivisionSortIndex({
    countryName,
    code: a.code,
  });
  const bSortIndex = getSubdivisionSortIndex({
    countryName,
    code: b.code,
  });

  if (aSortIndex !== null && bSortIndex !== null) {
    return aSortIndex - bSortIndex;
  }

  if (aSortIndex !== null) return -1;
  if (bSortIndex !== null) return 1;

  return a.name.localeCompare(b.name);
};
